import React, { useEffect, useState, useCallback } from "react";
import { columnsUserEnquiry } from "../../Global Functions/columnsAntd";
import { useDispatch, useSelector } from "react-redux";
import { getUserEnquiriesList } from "../../Redux/Actions/enquiriesAction";
import TableComponentsCp from "../../Global Functions/tableComponentsCp";
import DateRangePopup from "../../Utils/dateFilterPopup";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const UserEnquiries = () => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery); 
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);  
  const [dateRange, setDateRange] = useState({ start: null, end: null }); 

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
  const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  // Debounce searchQuery → debouncedSearch (500ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Reset to page 1 on search change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [debouncedSearch]);

  // fetchDetails with debounced search + date range
  const fetchDetails = useCallback(
    (page, limit, showLoader = true) => {
      const payload = {
        page,
        limit,
        is_deleted: false,
        filter: debouncedSearch,
        ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
        ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
      };
      dispatch(getUserEnquiriesList(payload, showLoader)).catch(() => {
        if (!showLoader) {
          toast.error("Failed to refresh User Enquiry List");
        }
      });
    },
    [dispatch, debouncedSearch, dateRange]  // dateRange added as dependency
  );

  useEffect(() => {
    fetchDetails(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, fetchDetails]);

  const { userEnquiries } = useSelector((state) => state.enquiries || {});
  const loading = useSelector((state) => state.rewards?.loading);

  useEffect(() => {
    if (userEnquiries?.total)
      setPagination((prevData) => ({ ...prevData, total: userEnquiries.total }));
  }, [userEnquiries]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // Date range apply handler
  const handleDateRangeApply = (start, end) => {
    if (start === null && end === null) {
      setDateRange({ start: null, end: null });
    } else {
      setDateRange({ start, end });
    }
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDownloadExcel = async () => {
    try {
      const payload = { all: true, is_deleted: false };
      const data = await dispatch(getUserEnquiriesList(payload));
      const rawData = data?.data?.data || [];

      if (!Array.isArray(rawData) || rawData.length === 0) {
        toast.error("No enquiry data available to download");
        return;
      }

      const preparedData = rawData.map((item) => ({
        name: item.user?.name || "",
        country_code: (item.user?.country_code || "").replace(/^\+/, ""),
        phone: item.user?.contact_number || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(preparedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "User Enquiries");
      XLSX.writeFile(workbook, "user_enquiries_list.xlsx");
    } catch (error) {
      toast.error("Failed to download enquiry data");
    }
  };

  return (
    <>
      <TableComponentsCp
        columns={columnsUserEnquiry}
        h3heading="User Enquiry List"
        pagination={pagination}
        dataSource={Array.isArray(userEnquiries?.data) ? userEnquiries.data : []}
        onChange={handleTableChange}
        setState={setSearchQuery}
        onCalendarClick={() => setIsDatePopupVisible(true)}    
        loading={loading}
        onDownloadUser={canAdd && canEdit ? handleDownloadExcel : null}
      />

      {/* Date Range Popup */}
      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />
    </>
  );
};

export default UserEnquiries;